import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { EditorState, StateEffect, Extension, Compartment } from '@codemirror/state';
import { EditorView, ViewUpdate, Decoration } from '@codemirror/view';
import { sql } from '@codemirror/lang-sql';
import { tags } from '@lezer/highlight';
import { Diagnostic } from '@codemirror/lint';

import {
  keymap,
  highlightSpecialChars,
  drawSelection,
  highlightActiveLine,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  lineNumbers,
  highlightActiveLineGutter,
} from '@codemirror/view';
import {
  HighlightStyle,
  defaultHighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  foldKeymap,
} from '@codemirror/language';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import {
  search,
  searchKeymap,
  highlightSelectionMatches,
} from '@codemirror/search';
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
  CompletionContext,
} from '@codemirror/autocomplete';
import { lintKeymap, setDiagnostics } from '@codemirror/lint';
import { CodeEditorService } from '@services/code-editor.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss'],
  standalone: false,
})
export class CodeEditorComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  /** Lets CodeMirror follow PrimeNG light/dark toggles (selection & built-in &dark/&light styles). */
  private readonly darkThemeCompartment = new Compartment();
  private readonly readOnlyCompartment = new Compartment();
  private themeObserver?: MutationObserver;
  @ViewChild('editor') editor: any;
  @Input() readOnly;
  @Input() processing = false;
  @Input() control;
  @Input() decorations = [];
  @Input() diagnostics: {
    content: string;
    message: string;
    severity: 'error' | 'warning';
  }[] = [];
  @Input() autocomplete: { type: string; label: string }[] = [];

  @Input() processedContent: {
    text: string;
    from: number;
    to: number;
  }; //processed content -> to be used to indicate previously used content if highlighted and used
  @Output() highlightedText = new EventEmitter<{
    text: string;
    from: number;
    to: number;
  }>(); //emits currently highlighted content along with its lines
  public currentlyHighlightedText = '';
  public editorView;
  public destroy$ = new Subject();
  public theme = EditorView.theme({
    '&.cm-editor.cm-focused': {
      outline: 'none',
    },
    '.cm-gutters': {
      backgroundColor: 'var(--layout-background-color)',
      color: 'var(--disabled-text-color)',
      border: 'none',
      borderRight: '1px solid var(--layout-border-color)',
    },
    '.cm-gutter': {
      color: 'var(--disabled-text-color)',
    },
    '.cm-lineNumbers': {
      color: 'var(--disabled-text-color)',
    },
    '.cm-activeLineGutter': {
      backgroundColor:
        'color-mix(in srgb, var(--layout-text-color) 10%, var(--layout-background-color))',
    },
    '.cm-gutterElement': {
      color: 'var(--disabled-text-color)',
      fontFamily: 'monospace',
      padding: '0px',
    },
    '.cm-error': {
      backgroundColor: 'rgba(194, 72, 72, 0.15);',
      color: 'var(--layout-text-color)',
    },
    '.cm-delete': {
      backgroundColor: 'rgba(194, 72, 72, 0.15);',
      color: 'var(--layout-text-color)',
    },
    '.cm-insert': {
      backgroundColor: 'rgba(47, 191, 125, 0.15);',
      color: 'var(--layout-text-color)',
    },
    '.cm-tooltip': {
      backgroundColor: 'var(--layout-background-color)',
      color: 'var(--layout-text-color)',
      border: '1px solid var(--layout-border-color)',
    },
    '.cm-tooltip-autocomplete': {
      '& > ul > li': {
        backgroundColor: 'var(--layout-background-color)',
        color: 'var(--layout-text-color)',
      },
      '& > ul > li[aria-selected]': {
        backgroundColor: 'var(--scaffold-accent-background-color)',
        color: 'var(--layout-text-color)',
      },
    },
    '.cm-tooltip-autocomplete .cm-completionMatchedText': {
      textDecoration: 'none !important',
      backgroundColor: 'var(--layout-background-color)',
      color: '#45a1e5',
    },
    '.cm-ProcessedLine': {
      textDecoration: 'underline',
    },
  });

  constructor(private codeEditorService: CodeEditorService) {}

  private isEditorReadOnly(): boolean {
    return !!this.readOnly || !!this.processing;
  }

  ngOnDestroy(): void {
    this.themeObserver?.disconnect();
    this.themeObserver = undefined;
    this.destroy$.next('');
  }

  private detectPrimengDarkMode(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    const el = document.documentElement;
    return (
      el.classList.contains('dark-theme') ||
      el.classList.contains('p-dark') ||
      el.getAttribute('data-p-theme') === 'dark'
    );
  }

  private syncEditorDarkTheme(): void {
    if (!this.editorView) {
      return;
    }
    const dark = this.detectPrimengDarkMode();
    this.editorView.dispatch({
      effects: this.darkThemeCompartment.reconfigure(
        EditorView.darkTheme.of(dark)
      ),
    });
  }

  ngOnChanges(changes) {
    if (!this.editorView) return;
    if (changes?.readOnly || changes?.processing) {
      this.editorView.dispatch({
        effects: this.readOnlyCompartment.reconfigure(
          EditorState.readOnly.of(this.isEditorReadOnly())
        ),
      });
    }
    // two way data binding essentially here since we cant apply a control directly onto the editor
    if (
      changes?.control?.currentValue &&
      changes?.control?.currentValue?.value !==
        changes?.control?.previousValue?.value
    ) {
      this.updateEditorContent();
    }

    // update for style changes like errors or warnings to be inserted
    if (changes?.diagnostics) this.updateDiagnostics();
    // if (changes?.processedContent) this.setProcessedContent(); //check out later, not enough time to sort out, meant for marking previuosly ran content, still need to sort out resetting it
  }

  ngAfterViewInit(): void {
    const highlightStyle = HighlightStyle.define([
      { tag: tags.keyword, class: 'cm-sql-keyword' },
      { tag: tags.comment, color: 'var(--disabled-text-color)' },
    ]);
    const highlightExtension = syntaxHighlighting(highlightStyle);
    const customCompletions = (context: CompletionContext) => {
      let word = context.matchBefore(/[\w.]+/);
      if (word?.from == word?.to && !context.explicit) return null;

      return {
        from: word?.from,
        options: this.autocomplete || [],
      };
    };

    const basicSetup: Extension = (() => [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightSpecialChars(),
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      bracketMatching(),
      closeBrackets(),
      autocompletion({ override: [customCompletions] }),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      search(),
      this.codeEditorService.createSearchMatchCountExtension(),
      sql(), // avoid if making for other langs...
      keymap.of([
        {
          key: 'Mod-Enter',
          run: () => true, // prevent default behavior of adding a new line when Cmd+Enter is pressed
        },
        indentWithTab,
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...completionKeymap,
        ...lintKeymap,
      ]),
    ])();

    const primengDarkMode = this.detectPrimengDarkMode();

    let editorElement = this.editor.nativeElement;
    let extensions = [
      basicSetup,
      this.theme,
      highlightExtension,
      this.darkThemeCompartment.of(EditorView.darkTheme.of(primengDarkMode)),
      this.readOnlyCompartment.of(
        EditorState.readOnly.of(this.isEditorReadOnly())
      ),
      EditorView.updateListener.of((v: ViewUpdate) => {
        if (this.isEditorReadOnly()) {
          return;
        }
        const selectedText = v.state.sliceDoc(
          v.state.selection.main.from,
          v.state.selection.main.to
        );

        if (this.currentlyHighlightedText !== selectedText) {
          this.currentlyHighlightedText = selectedText;
          this.highlightedText.emit({
            text: this.currentlyHighlightedText,
            from: v.state.selection.main.from,
            to: v.state.selection.main.to,
          });
        }
        if (v.docChanged) {
          this.clearDiagnostics();
          if (this?.control?.setValue) {
            this.control.setValue(v.state.doc.toString());
          }
        }
      }),
    ];

    let state!: EditorState;

    try {
      state = EditorState.create({
        doc: this?.control?.value || '',
        extensions,
      });
    } catch (e) {
      console.log('e', e);
    }

    this.editorView = new EditorView({
      state,
      parent: editorElement,
    });

    this.themeObserver = new MutationObserver(() => this.syncEditorDarkTheme());
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-p-theme'],
    });

    if (this.diagnostics?.length) {
      this.updateDiagnostics();
    }

    this.editorView.dispatch({
      effects: StateEffect.appendConfig.of(
        EditorView.decorations.of(Decoration.set(this.decorations))
      ),
    });
    this.codeEditorService.addContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(content => {
        const { state } = this.editorView;
        let { from, to } = state.selection.main;

        // avoid placing if yet set over the first content if available
        if (from === 0 && to === 0) {
          const initialConent = state?.doc?.text?.[0]?.length;
          if (initialConent) {
            from += initialConent + 1;
            to += initialConent + 1;
          }
        }

        const currentChar = state.doc.sliceString(from, to);
        if (currentChar.trim()) {
          this.editorView.dispatch({
            changes: {
              from,
              to,
              insert: ' ' + currentChar,
            },
          });
          from += 1;
          to += 1;
        }

        this.editorView.dispatch({
          changes: {
            from,
            to,
            insert: content,
          },
        });
      });
  }

  // Function to apply a class to a range
  applyClassToRange(from: number, to: number, className: string) {
    const doc = this.editorView.state.doc;
    const decorations = [];
    for (let i = from; i <= to; i++) {
      const line = doc.line(i);
      decorations.push(
        Decoration.line({
          class: 'cm-ProcessedLine',
        }).range(line.from)
      );
    }

    this.editorView.dispatch({
      effects: StateEffect.appendConfig.of(
        EditorView.decorations.of(Decoration.set(decorations))
      ),
    });
  }

  setProcessedContent() {
    if (!this.processedContent?.text) return;

    const doc = this.editorView.state.doc;
    const from = doc.lineAt(this.processedContent.from).number;
    const to = doc.lineAt(this.processedContent.to).number;

    // Create decoration for highlighted lines
    const decorations = [];
    for (let i = from; i <= to; i++) {
      const line = doc.line(i);
      decorations.push(
        Decoration.line({
          class: 'cm-ProcessedLine',
        }).range(line.from)
      );
    }

    this.editorView.dispatch({
      effects: StateEffect.appendConfig.of(
        EditorView.decorations.of(Decoration.set(decorations))
      ),
    });
  }

  // resets the editor content with control value
  updateEditorContent() {
    if (!this?.control) return;
    this.editorView.dispatch({
      changes: {
        from: 0,
        to: this.editorView.state.doc.length,
        insert: this.control.value,
      },
    });
  }

  clearDiagnostics() {
    this.editorView.dispatch(setDiagnostics(this.editorView.state, []));
  }

  updateDiagnostics() {
    const diagnostics: Diagnostic[] = this.getDiagnostics(
      this.editorView.state.doc.toString(),
      this.diagnostics
    );

    this.editorView.dispatch(
      setDiagnostics(this.editorView.state, diagnostics)
    );
  }

  getDiagnostics(doc: string, diagnosticsList = []): Diagnostic[] {
    const diagnostics: Diagnostic[] = [];
    const seen = new Set();
    // either work with the from/to provided or find it from the content provided instead for the error found
    // should also provide the from and if highlighted to also help determine where the error
    diagnosticsList.forEach(
      ({ content, severity, message, source, from, to }) => {
        if (
          typeof from === 'number' &&
          typeof to === 'number' &&
          from >= 0 &&
          to >= 1
        ) {
          diagnostics.push({
            from,
            to,
            severity,
            message: `${severity}: ${message}`,
          });
        } else {
          let fromContent = doc.indexOf(content);
          while (fromContent !== -1) {
            const to = fromContent + content.length;
            const key = `${fromContent}-${to}-${severity}-${message}-${source}`;
            if (!seen.has(key)) {
              diagnostics.push({
                from: fromContent,
                to,
                severity,
                message: `${severity}: ${message}`,
              });
              seen.add(key);
            }
            fromContent = doc.indexOf(content, to);
          }
        }
      }
    );

    return diagnostics;
  }
}
